import { useEffect, useState } from "react";
import { toast } from "sonner";
import { SpellCheck2, Plus, X, Loader2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";

/**
 * WO9 A1/A4 — the shared stock-phrase list. One list for every site, LocalPro staff only.
 * Phrases are flagged for the approver and never rewritten for them. No score, no detector reading.
 */
export function HygienePanel() {
  const [data, setData] = useState(null);
  const [add, setAdd] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/hygiene");
      setData(data);
    } catch {
      setData(false);     // not staff, or not available: the panel simply doesn't show
    }
  };
  useEffect(() => { load(); }, []);

  const save = async (phrases) => {
    setBusy(true);
    try {
      const { data: res } = await api.put("/admin/hygiene", { phrases });
      setData((d) => ({ ...d, phrases: res.phrases }));
      setAdd("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not save the list");
    } finally { setBusy(false); }
  };

  if (!data) return null;
  const phrases = data.phrases || [];

  return (
    <section className="rounded-lg border border-border bg-card p-6 lg:col-span-2" data-testid="hygiene-panel">
      <div className="mb-3 flex items-center gap-2 text-muted-foreground">
        <SpellCheck2 className="h-4 w-4" />
        <p className="text-xs font-semibold uppercase tracking-widest">Content hygiene — flagged phrases</p>
      </div>
      <p className="max-w-2xl text-sm text-muted-foreground" data-testid="hygiene-panel-note">{data.note}</p>
      <div className="mt-4 flex max-w-md gap-2">
        <Input value={add} placeholder="Add a phrase, e.g. “at the end of the day”"
          data-testid="hygiene-add-input"
          onChange={(e) => setAdd(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter" && add.trim()) save([...phrases, add.trim()]); }} />
        <Button disabled={busy || !add.trim()} data-testid="hygiene-add-btn"
          onClick={() => save([...phrases, add.trim()])}>
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
        </Button>
      </div>
      <div className="mt-4 flex flex-wrap gap-1.5" data-testid="hygiene-phrase-list">
        {phrases.map((p) => (
          <span key={p} className="inline-flex items-center gap-1 rounded-full border border-border bg-background px-2.5 py-1 text-xs"
            data-testid={`hygiene-phrase-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
            {p}
            <button className="opacity-60 transition-opacity hover:opacity-100" disabled={busy}
              onClick={() => save(phrases.filter((x) => x !== p))}
              data-testid={`hygiene-remove-${p.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}>
              <X className="h-3 w-3" />
            </button>
          </span>
        ))}
      </div>
      {data.updated_by && (
        <p className="mt-3 text-xs text-muted-foreground" data-testid="hygiene-updated-by">
          Last edited by {data.updated_by}
        </p>
      )}
    </section>
  );
}
